import { CurrencyPipe, NgFor, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { ConnectionStatus } from '@capacitor/network';
import { AlertFacade } from '@curbnturf/alert/src/lib/+state/alert.facade';
import { MapIconsComponent } from '@curbnturf/custom-svgs/src/lib/map-icons/map-icons.component';
import { MiscSvgComponent } from '@curbnturf/custom-svgs/src/lib/misc-svg/misc-svg.component';
import {
  DEFAULT_IMAGE_URL,
  FlowType,
  HelperFunctions,
  IListing,
  IPhoto,
  ISite,
  IUser,
  Listing,
  LISTING_INCOMPLETE_ALLOWED_DAYS,
  PhotoSizes,
  Site,
} from '@curbnturf/entities';
import { listingComplete } from '@curbnturf/helpers';
import { ListingFacade, ListingService } from '@curbnturf/listing';
import { OfflineListingService } from '@curbnturf/listing/src/lib/offline-listing.service';
import { Logger } from '@curbnturf/network';
import { PhotoService } from '@curbnturf/photo';
import { SignUpStateFacade } from '@curbnturf/sign-up-state/src/lib/+state/sign-up-state.facade';
import { SiteFacade, SiteService } from '@curbnturf/site';
import { HideConfirmationModalComponent } from '@curbnturf/site-components/src/lib/hide-confirmation-modal/hide-confirmation-modal.component';
import { StatusFacade } from '@curbnturf/status';
import { DeleteConfirmationModalComponent } from '@curbnturf/ui/src/lib/delete-confirmation-modal/delete-confirmation-modal.component';
import { UserFacade } from '@curbnturf/user/src/lib/+state/user.facade';
import { AlertController, IonicModule, LoadingController, ModalController, NavController } from '@ionic/angular';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';
import { EditSiteNameModalComponent } from '../../edit-site-name-modal/edit-site-name-modal.component';
import { EditSitePhotosModalComponent } from '../../edit-site-photos-modal/edit-site-photos-modal.component';

const { listingCompletePercentage, findSteps, findListingLatestStep } = HelperFunctions;

@Component({
  selector: 'curbnturf-listing-list-item',
  templateUrl: './listing-list-item.component.html',
  styleUrls: ['./listing-list-item.component.scss'],
  standalone: true,
  imports: [
    DeleteConfirmationModalComponent,
    HideConfirmationModalComponent,
    IonicModule,
    NgIf,
    MapIconsComponent,
    MiscSvgComponent,
    NgFor,
    RouterLink,
    CurrencyPipe,
  ],
})
export class ListingListItemComponent implements OnInit, OnDestroy, OnChanges {
  siteCompletePercentage = HelperFunctions.siteCompletePercentage;

  @Input() listing: Listing;

  @Output() listingRemoved: EventEmitter<string> = new EventEmitter<string>();
  @Output() listingUpdated: EventEmitter<Listing> = new EventEmitter<Listing>();

  public photoUrl: string = DEFAULT_IMAGE_URL;
  public photoCaption: string | undefined;

  public completePercent: number = 0;
  public incompleteMessages: string[] = [];

  user: IUser;

  connectionStatus: ConnectionStatus = { connected: true, connectionType: 'wifi' };

  subscriptions: Subscription[] = [];

  //@todo placeholders to be completed later
  listingRoute = '';
  boondockRoute = '';

  getPhotoUrl = HelperFunctions.getPhotoUrlSquareMedium;

  get multiSite(): boolean {
    if (this.listing?.sites) {
      return this.listing.sites?.length > 1;
    }
    return false;
  }

  get sites(): ISite[] {
    if (this.listing?.sites) {
      return this.listing.sites;
    }
    return [];
  }

  get publishable(): boolean {
    return Boolean(!this.isAvailable && this.listing.name && this.isComplete && !this.imagesIncomplete);
  }

  get notPublishable(): boolean {
    return Boolean(!this.isAvailable && (!this.listing.name || !this.isComplete));
  }

  get isComplete(): boolean {
    return this.completePercent > 98;
  }

  get isAvailable(): boolean {
    return Boolean(this.listing.available);
  }

  get isOnline() {
    return this.connectionStatus.connected;
  }

  get imagesIncomplete() {
    return !!(this.listing.publishDate && this.listing.photos && this.listing.photos?.length < 3);
  }

  get listingMissingImagePublishWindow(): boolean {
    if (!this.listing.publishDate) {
      return false;
    }
    return (
      DateTime.fromSeconds(this.listing.publishDate).plus({ days: LISTING_INCOMPLETE_ALLOWED_DAYS }).diffNow('days')
        .days >= 1
    );
  }

  get incompleteTimeRemaining() {
    if (this.listing.publishDate !== undefined) {
      return Math.max(
        Math.round(
          DateTime.fromSeconds(this.listing.publishDate).plus({ days: LISTING_INCOMPLETE_ALLOWED_DAYS }).diffNow('days')
            .days,
        ),
        0,
      );
    }
    return 0;
  }

  constructor(
    private alertFacade: AlertFacade,
    private loadingController: LoadingController,
    private logger: Logger,
    private offlineSiteService: OfflineListingService,
    private photoService: PhotoService,
    private router: Router,
    private signUpFacade: SignUpStateFacade,
    private listingFacade: ListingFacade,
    private statusFacade: StatusFacade,
    private userFacade: UserFacade,
    private listingService: ListingService,
    private alertController: AlertController,
    private siteService: SiteService,
    private modalController: ModalController,
    private navController: NavController,
    private siteFacade: SiteFacade,
  ) {}

  async ngOnInit() {
    if (this.photoUrl === DEFAULT_IMAGE_URL) {
      // Don't reload the image every time this component is viewed.
      await this.getPhotoUrlFromKey(this.listing?.photos?.[0]);
      const coverImage = await this.getCoverImage();
      this.photoUrl = coverImage.url;
      this.photoCaption = coverImage.caption;
    }
    this.subscriptions.push(
      this.statusFacade.networkStatus$.subscribe((status) => {
        this.connectionStatus = status;
      }),
      this.userFacade.currentUserOnce$.subscribe((user) => {
        this.user = user;
      }),
    );

    if (this.listing) {
      this.makeListingViewLink();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (!sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.listing.currentValue) {
      this.updateCompletionStatus(changes.listing.currentValue);
      this.makeListingViewLink();
    }
  }

  async getPhotoUrlFromKey(photo?: IPhoto): Promise<string | undefined> {
    if (photo) {
      if (this.isOnline) {
        return await this.photoService.getSavedPhotoUrl(photo, PhotoSizes.FlatSmall);
      } else {
        return await this.photoService.getPhotoOffline(photo);
      }
    }

    return DEFAULT_IMAGE_URL;
  }

  hide(listing: IListing) {
    return () => {
      return this.listingFacade.disable(listing);
    };
  }

  async removeSiteOffline(syncId: string) {
    this.subscriptions.push(
      this.offlineSiteService.remove(syncId).subscribe(() => {
        this.listingRemoved.emit(syncId);
      }),
    );
  }

  removeListing(id?: number | string): void {
    if (id) {
      this.listingFacade.remove(id);
      this.listingRemoved.emit(this.listing.syncId);
    }
  }

  removeListingFunction(id?: number | string): () => void {
    if (this.connectionStatus.connected && this.listing?.id) {
      return () => this.removeListing(id);
    } else {
      return () => this.removeSiteOffline(id as string);
    }
  }

  publish(listing: Listing) {
    return () => {
      return this.listingFacade.enable(listing);
    };
  }

  async resumeWizard() {
    const loading = await this.loadingController.create({
      message: 'Loading Listing Information...',
      duration: 15000,
      backdropDismiss: true,
    });

    await loading.present();
    if (this.listing.id) {
      // Load in the complete listing info. This helps both ensure that the complete record is loaded
      // and all of its parts are up-to-date.
      this.subscriptions.push(
        this.listingService.fetch(this.listing.id).subscribe({
          next: async (listing: IListing) => {
            const flow: FlowType = FlowType.listing;

            let selectedSite: ISite | undefined = undefined;
            if (listing.sites && Array.isArray(listing.sites) && listing.sites.length > 0) {
              selectedSite = listing.sites.slice().pop();
            }

            const steps = findSteps(flow, this.listing, this.listing.user);
            const latestStepType =
              findListingLatestStep(flow, listing, this.listing.user, selectedSite) || steps[0].type;
            const currentStep = steps.find((el) => el.type === latestStepType);

            if (currentStep) {
              this.signUpFacade.resetProcess();
              this.signUpFacade.setInProgressListing(listing);
              this.signUpFacade.setState({
                user: this.user,
                listing,
                steps,
                currentStep,
                spinner: false,
                selectedSite,
              });
              await this.router.navigate(currentStep.route);
            } else {
              this.logger.error('Failed to find current step to resume listing.');
              this.alertFacade.display({
                title: 'Failed to Resume Site',
                body: 'Failed to resume listing. Could not find where to go!',
                level: 'error',
              });
            }
          },
          error: () => {
            loading.dismiss();
          },
          complete: () => {
            loading.dismiss();
          },
        }),
      );
    }
  }

  makeListingViewLink() {
    if (this.listing.id) {
      this.listingRoute = HelperFunctions.buildListingRoute(this.listing).join('/');
    }
  }

  updateCompletionStatus(listing: IListing) {
    // Select a site to use for the completeness check - this could be augmented by checking all sites for completion
    // and choosing the most incomplete one, based on some criteria.
    let selectedSite: ISite | undefined = listing.sites?.filter((site) => site.available).pop();
    // If an available site was not found, try to find an incomplete site.
    if (!selectedSite) {
      selectedSite = listing.sites?.slice().pop();
    }
    this.completePercent = listingCompletePercentage(FlowType.listing, listing, listing?.user, selectedSite);
    this.incompleteMessages = listingComplete.listingPercentComplete(listing).messages;
  }

  getName(): string | undefined {
    if (this.listing) {
      return this.listing.getName();
    }

    return '';
  }

  async getCoverImage(): Promise<{ url: string; caption: string | undefined }> {
    if (this.listing) {
      if (this.listing.photos && this.listing.photos.length > 0) {
        return {
          url: (await this.getPhotoUrlFromKey(this.listing.photos[0])) ?? DEFAULT_IMAGE_URL,
          caption: this.listing.photos[0].caption,
        };
      }
    }

    return {
      url: DEFAULT_IMAGE_URL,
      caption: undefined,
    };
  }

  async onToggleSiteEnable(site: ISite) {
    if (site.available) {
      const alert = await this.alertController.create({
        header: `Disable Site "${site.name}"?`,
        message: `Are you sure you want to disable the site "${site.name}"?`,
        buttons: [
          {
            role: 'cancel',
            text: 'Cancel',
          },
          {
            role: 'confirm',
            text: 'Yes, Disable Site',
            handler: () => {
              this.siteService.disable(site).subscribe(async (results) => {
                if (results.success) {
                  const sites = this.listing.sites?.slice() || [];
                  const foundIndex = sites.findIndex((findSite) => findSite.id === site.id);

                  if (foundIndex) {
                    const newSite = new Site({ ...sites[foundIndex], available: false });
                    sites.splice(foundIndex, 1, newSite);
                    this.listing.sites = sites;
                  }
                } else {
                  const errorAlert = await this.alertController.create({
                    header: `Error Disabling Site "${site.name}"?`,
                    message: `There was an error: "${results.message}"?`,
                  });

                  await errorAlert.present();
                }
              });
            },
          },
        ],
      });

      await alert.present();
    } else {
      const alert = await this.alertController.create({
        header: `Enable Site "${site.name}"?`,
        message: `Are you sure you want to enable the site "${site.name}"?`,
        buttons: [
          {
            role: 'cancel',
            text: 'Cancel',
          },
          {
            role: 'confirm',
            text: 'Yes, Enable Site',
            handler: () => {
              this.siteService.enable(site).subscribe(async (results) => {
                if (results.success) {
                  const sites = this.listing.sites?.slice() || [];
                  const foundIndex = sites.findIndex((findSite) => findSite.id === site.id);

                  if (foundIndex) {
                    const newSite = new Site({ ...sites[foundIndex], available: true });
                    sites.splice(foundIndex, 1, newSite);
                    this.listing.sites = sites;
                  }
                } else {
                  const errorAlert = await this.alertController.create({
                    header: `Error Enabling Site "${site.name}"?`,
                    message: `There was an error: "${results.message}"?`,
                  });

                  await errorAlert.present();
                }
              });
            },
          },
        ],
      });

      await alert.present();
    }
  }

  async onUploadSitePhotos(site: ISite) {
    const imageModal = await this.modalController.create({
      component: EditSitePhotosModalComponent,
      componentProps: {
        site,
      },
    });

    await imageModal.present();

    imageModal.onDidDismiss().then(() => {
      this.refreshSite(site);
    });
  }

  async onEditSiteName(site: ISite) {
    const editModal = await this.modalController.create({
      component: EditSiteNameModalComponent,
      componentProps: {
        site,
      },
    });

    await editModal.present();

    editModal.onDidDismiss().then(() => {
      this.refreshSite(site);
    });
  }

  async refreshSite(site: ISite) {
    if (site && site.id) {
      this.siteService.fetch(site.id).subscribe((returnedSite) => {
        const sites: Site[] = this.listing.sites?.slice() || [];

        const updatedSite: Site = new Site(returnedSite);
        const foundIndex: number = sites.findIndex((findSite) => findSite.id === site.id);
        if (foundIndex !== -1) {
          sites.splice(foundIndex, 1, updatedSite);
        } else if (returnedSite.listing && returnedSite.listing.id === site.listing?.id) {
          sites.push(updatedSite);
        }

        this.listing.sites = sites;
      });
    }
  }

  async onEditSite(site: ISite) {
    if (Capacitor.isNativePlatform()) {
      this.siteFacade.selectSite(site);
      await this.navController.navigateForward([
        '/properties',
        'edit',
        this.listing.id ?? this.listing?.syncId,
        'overview',
      ]);
    } else {
      await this.router.navigate([
        '/properties',
        'edit',
        this.listing.id ?? this.listing?.syncId,
        { section: 'overview', site: site.id ?? site.syncId },
      ]);
    }
  }
}
